import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { useTeams } from '@/hooks/useTeams';
import { useTeamEfficiency } from '@/hooks/kpi/useTeamEfficiency';
import { BarChart3, Clock, CheckCircle2, RotateCcw, TrendingUp, TrendingDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { format, subDays } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';
// DTC-K: route the team-analytics window anchors + shop-day end label
// through canonical helpers. Pre-DTC-K used `format(new Date(),
// 'yyyy-MM-dd')` browser-local for `endDate` — could surface
// yesterday/tomorrow during evening for cross-TZ viewers.
import { clock } from '@/utils/clock';
import { getTodayInShopTz } from '@/utils/shopTime';

interface TeamStats {
  teamId: string;
  teamName: string;
  isDefault: boolean;
  ticketsCompleted: number;
  avgCompletionMinutes: number;
  efficiencyRate: number | null;
  comebackCount: number;
  totalLaborMinutes: number;
}

export function TeamAnalyticsCard() {
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const { teams, isLoading: teamsLoading } = useTeams();
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  const startDate = format(
    subDays(clock.now(), dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90),
    'yyyy-MM-dd'
  );
  const endDate = getTodayInShopTz(shopTimezone);

  // K-T-13-B: per-team efficiency + display totals sourced from
  // canonical `kpi_efficiency_by_team` RPC. Replaces the prior per-team
  // tickets fetch + inline reduce + Math.round((SUM(predicted) /
  // SUM(actual)) * 100) with the canonical SUM/SUM ratio + sample
  // gate. Behavior change: teams with fewer than 3 qualifying tickets
  // (predicted IS NOT NULL AND actual IS NOT NULL AND actual > 0) now
  // render an "—" placeholder instead of a noisy single-row ratio
  // (and the previous silent default to 100% when totalPredicted = 0
  // is gone). Window also shifts from `scheduled_date` (date column,
  // inclusive) to `completed_at` (half-open, canonical).
  const { data: efficiencyRows, isLoading: efficiencyLoading } = useTeamEfficiency({
    shopId: activeShop?.id ?? '',
    startDate,
    endDate,
    shopTimezone,
  });

  // Per-team comeback count + total labor minutes stay inline — out of
  // K-T-13-B scope (comeback logic untouched; `get_team_labor_totals`
  // is a separate RPC that already aggregates correctly server-side).
  const { data: extraData, isLoading: extraLoading } = useQuery({
    queryKey: ['team-analytics-extras', activeShop?.id, startDate, endDate],
    queryFn: async () => {
      if (!activeShop?.id || !teams?.length) return new Map<string, { comebackCount: number; totalLaborMinutes: number }>();

      const result = new Map<string, { comebackCount: number; totalLaborMinutes: number }>();

      for (const team of teams.filter(t => t.is_active)) {
        // Comeback count (per-team) — preserved verbatim from pre-K-T-13-B.
        const { data: comebackTickets } = await supabase
          .from('tickets')
          .select('id', { count: 'exact', head: false })
          .eq('team_id', team.id)
          .eq('status', 'Completed')
          // CSI-S1: exclude soft-deleted Completed tickets from team comeback counts.
          .is('deleted_at', null)
          .eq('is_comeback', true)
          .gte('scheduled_date', startDate)
          .lte('scheduled_date', endDate);
        const comebackCount = comebackTickets?.length || 0;

        // Total labor minutes via the existing get_team_labor_totals RPC.
        let totalLaborMinutes = 0;
        try {
          const { data: laborData } = await supabase.rpc('get_team_labor_totals', {
            p_team_id: team.id,
            p_start_date: startDate,
            p_end_date: endDate,
          });
          const labor = laborData as { total_actual_minutes?: number; actual_hours?: number } | null;
          // get_team_labor_totals returns `actual_hours` in jsonb; convert to minutes.
          totalLaborMinutes = labor?.total_actual_minutes
            ?? Math.round((labor?.actual_hours ?? 0) * 60);
        } catch (e) {
          totalLaborMinutes = 0;
        }

        result.set(team.id, { comebackCount, totalLaborMinutes });
      }

      return result;
    },
    enabled: !!activeShop?.id && !!teams?.length,
  });

  const isLoading = efficiencyLoading || extraLoading;

  const statsData: TeamStats[] = (efficiencyRows ?? [])
    .map((r) => {
      const extras = extraData?.get(r.team_id);
      // Total labor falls back to the canonical actual-minutes total
      // when get_team_labor_totals is unavailable, matching the
      // pre-K-T-13-B fallback.
      const totalLaborMinutes = extras?.totalLaborMinutes ?? r.total_actual_minutes;
      return {
        teamId: r.team_id,
        teamName: r.team_name,
        isDefault: r.is_default,
        ticketsCompleted: r.ticket_count,
        avgCompletionMinutes: r.ticket_count > 0
          ? Math.round(r.total_actual_minutes / r.ticket_count)
          : 0,
        efficiencyRate: r.gated || r.ratio === null
          ? null
          : Math.round(r.ratio * 100),
        comebackCount: extras?.comebackCount ?? 0,
        totalLaborMinutes,
      };
    })
    .sort((a, b) => b.ticketsCompleted - a.ticketsCompleted);

  if (teamsLoading || isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Team Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!statsData?.length) {
    return null;
  }

  const formatMinutes = (mins: number) => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Team Performance Analytics
            </CardTitle>
            <CardDescription>
              Comparative team metrics and efficiency
            </CardDescription>
          </div>
          <Select value={dateRange} onValueChange={(v: '7d' | '30d' | '90d') => setDateRange(v)}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {statsData.map((team, index) => (
            <div
              key={team.teamId}
              className="p-4 rounded-lg border bg-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">{team.teamName}</span>
                  {team.isDefault && (
                    <Badge variant="secondary" className="text-xs">Default</Badge>
                  )}
                  {index === 0 && statsData.length > 1 && (
                    <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                      Top Performer
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground text-sm">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span>Completed</span>
                  </div>
                  <p className="text-xl font-bold">{team.ticketsCompleted}</p>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground text-sm">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Avg Time</span>
                  </div>
                  <p className="text-xl font-bold">{formatMinutes(team.avgCompletionMinutes)}</p>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground text-sm">
                    {team.efficiencyRate === null ? (
                      <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : team.efficiencyRate >= 100 ? (
                      <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                    )}
                    <span>Efficiency</span>
                  </div>
                  <p className={`text-xl font-bold ${
                    team.efficiencyRate === null
                      ? 'text-muted-foreground'
                      : team.efficiencyRate >= 100
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                  }`}>
                    {team.efficiencyRate === null ? '—' : `${team.efficiencyRate}%`}
                  </p>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground text-sm">
                    <RotateCcw className="h-3.5 w-3.5" />
                    <span>Comebacks</span>
                  </div>
                  <p className={`text-xl font-bold ${
                    team.comebackCount === 0 ? 'text-emerald-600' : 'text-amber-600'
                  }`}>
                    {team.comebackCount}
                  </p>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t text-sm text-muted-foreground">
                Total labor: {formatMinutes(team.totalLaborMinutes)}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
